import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../lib/api";

export default function StaticPage() {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api.get(`/pages/${slug}`)
            .then(({ data }) => setPage(data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        if (page?.seo_title) {
            document.title = page.seo_title;
        }
    }, [page]);

    if (loading) {
        return <div className="grid min-h-[60vh] place-items-center bg-obsidian text-gold">Loading…</div>;
    }
    if (notFound || !page) {
        return (
            <div className="grid min-h-[60vh] place-items-center bg-obsidian px-6 text-center">
                <div>
                    <p className="font-display text-4xl text-white">Lost in the atelier.</p>
                    <p className="mt-2 text-white/50">We couldn't find that page.</p>
                    <Link to="/" className="mt-6 inline-block bg-gold px-6 py-3 text-[11px] uppercase tracking-[0.3em] text-obsidian">
                        Home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-obsidian" data-testid="static-page">
            <div className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
                <p className="text-[11px] uppercase tracking-[0.4em] text-gold">LovinLife</p>
                <h1 className="mt-3 font-display text-4xl text-white sm:text-6xl">{page.title}</h1>
                {page.subtitle && <p className="mt-4 text-base text-white/60 sm:text-lg">{page.subtitle}</p>}

                <article className="prose prose-invert mt-12 max-w-none font-body text-white/80">
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            h1: ({ node, ...p }) => <h1 className="font-display text-3xl text-white sm:text-4xl mt-12 mb-4" {...p} />,
                            h2: ({ node, ...p }) => <h2 className="font-display text-2xl text-white sm:text-3xl mt-10 mb-3" {...p} />,
                            h3: ({ node, ...p }) => <h3 className="font-display text-xl text-gold mt-8 mb-2" {...p} />,
                            p: ({ node, ...p }) => <p className="leading-relaxed mb-4 text-white/80" {...p} />,
                            a: ({ node, ...p }) => <a className="text-gold underline underline-offset-4 hover:text-gold-light" {...p} />,
                            ul: ({ node, ...p }) => <ul className="my-4 ml-6 list-disc space-y-2 text-white/80" {...p} />,
                            ol: ({ node, ...p }) => <ol className="my-4 ml-6 list-decimal space-y-2 text-white/80" {...p} />,
                            li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
                            strong: ({ node, ...p }) => <strong className="text-white" {...p} />,
                            em: ({ node, ...p }) => <em className="italic text-gold" {...p} />,
                            blockquote: ({ node, ...p }) => (
                                <blockquote className="my-6 border-l-2 border-gold pl-6 font-display text-xl text-white/90" {...p} />
                            ),
                            img: ({ node, ...p }) => <img className="my-6 w-full" alt="" {...p} />,
                            hr: () => <hr className="my-10 border-border-dim" />,
                        }}
                    >
                        {page.body || ""}
                    </ReactMarkdown>
                </article>
            </div>
        </div>
    );
}
